import PropTypes from 'prop-types';
import { TextWithChips } from './placeholders';
import styles from './outreach.module.scss';

// Read-only, plain-language view of a rule, built from the definition and the catalogue.

const NO_VALUE_OPS = ['isEmpty', 'notEmpty'];

const valueText = (fact, value) => {
  if (fact.type === 'boolean') return value ? 'da' : 'ne';
  const one = (v) => (fact.values && fact.values[v]) || String(v);
  if (Array.isArray(value)) {
    return value.length > 5
      ? `${value.length} stavki`
      : value.map(one).join(', ');
  }
  const unit = fact.unit ? ` ${fact.unit}` : '';
  return fact.type === 'enum' ? one(value) : `${value}${unit}`;
};

// Returns null when a fact or operator is missing from the catalogue.
const conditionText = (catalog, c) => {
  const fact = catalog.facts.find((f) => f.key === c.fact);
  const op = catalog.ops.find((o) => o.key === c.op);
  if (!fact || !op) return null;
  const tail = NO_VALUE_OPS.includes(c.op) ? '' : valueText(fact, c.value);
  return [fact.label, op.label, tail].filter(Boolean).join(' ');
};

const Conditions = ({ catalog, block, nested }) => {
  const items = [
    ...block.conditions.map((c) => ({ text: conditionText(catalog, c) })),
    ...(block.groups || []).map((g) => ({ group: g })),
  ];
  return (
    <ul className={`${styles.sumList} ${nested ? styles.sumNested : ''}`}>
      {items.map((it, i) => (
        <li key={i}>
          {it.group ? (
            <Conditions catalog={catalog} block={it.group} nested />
          ) : (
            it.text
          )}
        </li>
      ))}
    </ul>
  );
};

Conditions.propTypes = {
  catalog: PropTypes.object.isRequired,
  block: PropTypes.object.isRequired,
  nested: PropTypes.bool,
};

const allKnown = (catalog, block) =>
  [
    ...block.conditions,
    ...(block.groups || []).flatMap((g) => g.conditions),
  ].every((c) => conditionText(catalog, c) !== null);

const RuleSummary = ({ catalog, rule, pseudocode }) => {
  const def = rule.definition || rule;
  const kind = catalog.kinds.find((k) => k.key === def.kind);
  const repeat =
    def.repeat?.mode === 'once'
      ? 'samo jednom po korisniku'
      : `najviše jednom u ${def.repeat?.days} dana`;

  if (!allKnown(catalog, def.when)) {
    return <pre className={styles.pseudo}>{pseudocode}</pre>;
  }

  return (
    <dl className={styles.summary}>
      <dt>Kome</dt>
      <dd>
        <span className={styles.dim}>
          Važi ako je ispunjeno {def.when.mode === 'any' ? 'bar jedno' : 'sve'}{' '}
          od:
        </span>
        <Conditions catalog={catalog} block={def.when} />
      </dd>
      <dt>Poruka</dt>
      <dd>
        {kind ? kind.label : def.kind}, prioritet {def.priority}
      </dd>
      <dt>Ponavljanje</dt>
      <dd>{repeat}</dd>
      <dt>Uputstvo za AI</dt>
      <dd>
        <TextWithChips catalog={catalog} text={def.instruction} />
      </dd>
      <dt>Rezervni tekst</dt>
      <dd>
        <TextWithChips catalog={catalog} text={def.fallback} />
      </dd>
    </dl>
  );
};

RuleSummary.propTypes = {
  catalog: PropTypes.object.isRequired,
  rule: PropTypes.object.isRequired,
  pseudocode: PropTypes.string,
};

export default RuleSummary;
